/**
 * Subscription Onboarding Page
 * Users must accept terms and subscribe before accessing the platform
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Check,
  Crown,
  Zap,
  Star,
  Shield,
  FileText,
  Tag,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowRight,
  Users,
  Calendar,
  Fingerprint,
  BarChart3,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Subscription plans
const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 149,
    clients: "Up to 10",
    icon: Star,
    color: "bg-blue-500",
    features: [
      "Up to 10 active clients",
      "Unlimited caregivers",
      "EVV & GPS tracking",
      "Basic scheduling",
      "Client & employee management",
      "Email support",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    price: 349,
    clients: "11-49",
    icon: Zap,
    color: "bg-emerald-500",
    popular: true,
    features: [
      "11-49 active clients",
      "Everything in Starter",
      "Advanced scheduling",
      "Referral management",
      "HR & payroll tools",
      "Priority support",
      "Custom reports",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 549,
    clients: "50+",
    icon: Crown,
    color: "bg-purple-500",
    features: [
      "50+ active clients",
      "Everything in Growth",
      "Multi-location support",
      "API access",
      "Dedicated success manager",
      "Custom integrations",
      "Phone support",
    ],
  },
];

export default function SubscriptionPage() {
  const { user, token, updateSubscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1); // 1: Terms, 2: Plan Selection, 3: Checkout
  const [agreed, setAgreed] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState(null);
  const [couponDetails, setCouponDetails] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [billingIssue, setBillingIssue] = useState(null); // existing customer whose payment failed / access paused
  const [portalBusy, setPortalBusy] = useState(false);

  // Axios config with auth header
  const authHeaders = { Authorization: `Bearer ${token}` };

  const api = {
    get: (url) => axios.get(`${API}${url}`, { headers: authHeaders }).then(r => r.data),
    post: (url, data) => axios.post(`${API}${url}`, data, { headers: authHeaders }).then(r => r.data),
  };

  // Check if already subscribed or returning from Stripe checkout
  useEffect(() => {
    checkSubscription();
    
    // Handle return from Stripe checkout
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [token]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment verification timed out. Please refresh or contact support.");
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const data = await api.get(`/billing/checkout/status/${sessionId}`);
      
      if (data.status === "paid") {
        toast.success("Payment successful! Welcome to CarePilot.");
        updateSubscriptionStatus(true, null);
        setCheckingPayment(false);
        navigate("/dashboard", { replace: true });
        return;
      } else if (data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      } else {
        toast.error("Error verifying payment. Please refresh the page.");
        setCheckingPayment(false);
      }
    }
  };

  const checkSubscription = async () => {
    if (!token) return;
    try {
      const data = await api.get('/billing/subscription');
      if (data?.status === "active" || data?.status === "trialing") {
        // Already subscribed, go to dashboard
        navigate("/dashboard");
      } else if (data?.stripe_customer_id && (data?.access_paused || ["past_due", "paused", "unpaid"].includes(data?.status))) {
        setBillingIssue(data);
        if (searchParams.get("update_card")) openPortal();
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const data = await api.post('/billing/portal', { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not open the billing portal");
      setPortalBusy(false);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponStatus("validating");
    setCouponError("");
    setCouponDetails(null);
    
    try {
      const data = await api.post('/billing/validate-coupon', {
        coupon_code: couponCode.trim()
      });
      
      if (data.valid) {
        setCouponStatus("valid");
        setCouponDetails(data);
      } else {
        setCouponStatus("invalid");
        setCouponError(data.error || "Invalid coupon code");
      }
    } catch (error) {
      setCouponStatus("invalid");
      setCouponError("Could not validate coupon");
    }
  };

  const handleAcceptTerms = () => {
    if (agreed) {
      setStep(2);
    }
  };

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    setStep(3);
  };

  const handleCheckout = async () => {
    if (!selectedPlan) return;
    
    setProcessing(true);
    try {
      // Step 1: Record terms acceptance
      await api.post('/billing/accept-terms', {
        plan_id: selectedPlan,
        accepted_at: new Date().toISOString()
      });

      // Step 2: Create checkout session
      const checkoutData = {
        plan_id: selectedPlan,
        origin_url: window.location.origin,
        return_path: "/subscribe",
      };
      
      if (couponStatus === "valid" && couponCode.trim()) {
        checkoutData.coupon_code = couponCode.trim();
      }

      const data = await api.post('/billing/checkout', checkoutData);
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("No checkout URL returned. Please try again.");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const msg = error.response?.data?.detail || error.message || "Checkout failed. Please try again.";
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  };

  const selectedPlanDetails = PLANS.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">CarePilot</h1>
              <p className="text-xs text-slate-400">Home Care Management</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-400">Welcome,</p>
            <p className="text-white font-medium">{user?.name || user?.email}</p>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {billingIssue && (
          <Card className="bg-rose-950/40 border-rose-700 mb-8" data-testid="billing-issue-banner">
            <CardContent className="flex flex-col sm:flex-row sm:items-center gap-4 py-5">
              <div className="flex-1">
                <p className="text-white font-semibold">
                  {billingIssue.access_paused ? "Access is paused — payment required" : "Your last payment failed"}
                </p>
                <p className="text-sm text-rose-200">
                  Update your card in the secure Stripe portal and access is restored automatically once the payment succeeds. Caregivers can keep clocking in meanwhile.
                </p>
              </div>
              <Button onClick={openPortal} disabled={portalBusy} className="bg-rose-600 hover:bg-rose-700 gap-2" data-testid="update-payment-method-btn">
                {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Update payment method
              </Button>
            </CardContent>
          </Card>
        )}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { num: 1, label: "Terms & Conditions" },
            { num: 2, label: "Select Plan" },
            { num: 3, label: "Payment" },
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center gap-2 ${step >= s.num ? "text-primary" : "text-slate-500"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step > s.num ? "bg-primary text-white" :
                  step === s.num ? "bg-primary/20 text-primary border-2 border-primary" :
                  "bg-slate-700 text-slate-400"
                }`}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className="text-sm font-medium hidden sm:block">{s.label}</span>
              </div>
              {idx < 2 && (
                <div className={`w-12 h-0.5 mx-2 ${step > s.num ? "bg-primary" : "bg-slate-700"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Payment Verification Overlay */}
        {checkingPayment && (
          <Card className="bg-slate-800/50 border-slate-700 max-w-md mx-auto">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Verifying Payment...</h2>
              <p className="text-slate-400 text-center text-sm">
                Please wait while we confirm your payment with Stripe. This may take a few moments.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Terms & Conditions */}
        {!checkingPayment && step === 1 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Terms & Conditions
              </CardTitle>
              <CardDescription className="text-slate-400">
                Please review and accept our terms before continuing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px] pr-4 mb-6">
                <div className="space-y-6 text-sm text-slate-300">
                  <section>
                    <h3 className="font-bold text-white mb-2">CarePilot USA – Terms and Conditions</h3>
                    <p className="text-xs text-slate-500 mb-4">Effective Date: January 2026</p>
                    
                    <p className="mb-4">
                      These Terms and Conditions ("Terms") govern access to and use of the CarePilot USA Home Care Software Platform 
                      ("CarePilot," "Platform," "Service," "we," "our," or "us"). CarePilot USA is a company operating in the United States.
                    </p>
                    
                    <p className="mb-4 font-medium text-white">
                      By creating an account, clicking "I Agree," or submitting payment, you ("Customer," "you," or "your") acknowledge 
                      that you have read, understood, and agree to be legally bound by these Terms.
                    </p>
                  </section>

                  <section>
                    <h4 className="font-semibold text-white mb-2">1. Eligibility & Authority</h4>
                    <p>By using the Platform, you represent and warrant that:</p>
                    <ul className="list-disc ml-5 mt-2 space-y-1">
                      <li>You are at least 18 years old.</li>
                      <li>You have the legal authority to bind the home care agency or organization you represent.</li>
                      <li>All registration and billing information provided is accurate, current, and complete.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="font-semibold text-white mb-2">2. Subscription Plans & Client Limits</h4>
                    <p>CarePilot USA subscriptions are based on active client count:</p>
                    <ul className="list-disc ml-5 mt-2 space-y-1">
                      <li><strong className="text-white">Tier 1 (Starter):</strong> Up to 10 active clients - $149/month</li>
                      <li><strong className="text-white">Tier 2 (Growth):</strong> 11–49 active clients - $349/month</li>
                      <li><strong className="text-white">Tier 3 (Enterprise):</strong> 50 or more active clients - $549/month</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="font-semibold text-white mb-2">3. Fees, Billing & Payment</h4>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>All fees are billed in advance monthly.</li>
                      <li>New subscriptions include a 7-day free trial. A valid payment method is required at signup; the first monthly fee is charged automatically when the trial ends unless you cancel before then.</li>
                      <li>All prices are listed in U.S. Dollars (USD).</li>
                      <li>You authorize CarePilot USA to charge your selected payment method.</li>
                      <li>Failure to pay may result in suspension or termination of service.</li>
                    </ul>
                  </section>

                  <section className="bg-red-900/30 p-4 rounded-lg border border-red-800">
                    <h4 className="font-semibold text-red-300 mb-2">4. No Refund Policy</h4>
                    <p className="text-red-200">To the fullest extent permitted by U.S. law:</p>
                    <ul className="list-disc ml-5 mt-2 space-y-1 text-red-200">
                      <li>All subscription fees, setup fees, and add-ons are <strong>non-refundable</strong>.</li>
                      <li>No refunds or credits are issued for partial months, unused services, or downgrades.</li>
                      <li>Cancellation does not relieve you of payment obligations already incurred.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="font-semibold text-white mb-2">5. Data Ownership & Privacy</h4>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>You retain ownership of all data you upload to the Platform.</li>
                      <li>CarePilot USA processes data solely to provide the Service.</li>
                      <li>Reasonable administrative, technical, and physical safeguards are used to protect data.</li>
                      <li>You are responsible for ensuring lawful data collection and consent.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className="font-semibold text-white mb-2">6. Limitation of Liability</h4>
                    <p>To the maximum extent permitted under U.S. law:</p>
                    <ul className="list-disc ml-5 mt-2 space-y-1">
                      <li>CarePilot USA is not liable for indirect, incidental, or consequential damages.</li>
                      <li>Total liability shall not exceed the amount paid by you in the 12 months preceding the claim.</li>
                    </ul>
                  </section>

                  <section className="bg-slate-700/50 p-4 rounded-lg">
                    <h4 className="font-semibold text-white mb-2">7. Acceptance</h4>
                    <p>By clicking "I Agree," creating an account, or submitting payment, you confirm that you have read, understood, and agreed to these Terms and Conditions.</p>
                  </section>
                </div>
              </ScrollArea>

              <div className="border-t border-slate-700 pt-4">
                <div className="flex items-start space-x-3 mb-4">
                  <Checkbox 
                    id="terms-agree" 
                    checked={agreed} 
                    onCheckedChange={setAgreed}
                    className="border-slate-500 data-[state=checked]:bg-primary"
                  />
                  <label 
                    htmlFor="terms-agree" 
                    className="text-sm leading-tight cursor-pointer text-slate-300"
                  >
                    I have read and agree to the Terms and Conditions. I understand there are{" "}
                    <strong className="text-red-400">no refunds</strong> and I am entering into a legally binding agreement.
                  </label>
                </div>

                <Button 
                  onClick={handleAcceptTerms} 
                  disabled={!agreed}
                  className="w-full"
                  size="lg"
                >
                  Continue to Plan Selection
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Plan Selection */}
        {!checkingPayment && step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Choose Your Plan</h2>
              <p className="text-slate-400">Select the plan that fits your agency's needs</p>
              <p className="mt-2 inline-flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1" data-testid="plan-trial-badge">7-day free trial on every plan · card required · first charge after the trial</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {PLANS.map((plan) => {
                const Icon = plan.icon;
                return (
                  <Card 
                    key={plan.id}
                    className={`relative bg-slate-800/50 border-slate-700 hover:border-primary/50 transition-all cursor-pointer ${
                      plan.popular ? "ring-2 ring-primary" : ""
                    }`}
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    {plan.popular && (
                      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                        Most Popular
                      </Badge>
                    )}
                    <CardHeader className="text-center pb-2">
                      <div className={`w-14 h-14 rounded-xl ${plan.color} flex items-center justify-center mx-auto mb-3`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <CardTitle className="text-white">{plan.name}</CardTitle>
                      <CardDescription className="text-slate-400">{plan.clients} clients</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                      <div className="mb-4">
                        <span className="text-4xl font-bold text-white">${plan.price}</span>
                        <span className="text-slate-400">/month</span>
                      </div>
                      <ul className="space-y-2 text-sm text-left mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-300">
                            <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className="w-full" 
                        variant={plan.popular ? "default" : "outline"}
                      >
                        Select {plan.name}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Button 
              variant="ghost" 
              className="text-slate-400"
              onClick={() => setStep(1)}
            >
              ← Back to Terms
            </Button>
          </div>
        )}

        {/* Step 3: Checkout with Coupon */}
        {!checkingPayment && step === 3 && selectedPlanDetails && (
          <Card className="bg-slate-800/50 border-slate-700 max-w-lg mx-auto">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <selectedPlanDetails.icon className="w-5 h-5 text-primary" />
                Complete Your Subscription
              </CardTitle>
              <CardDescription className="text-slate-400">
                You selected the {selectedPlanDetails.name} plan
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Order Summary */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h3 className="font-medium text-white mb-3">Order Summary</h3>
                <div className="flex justify-between text-slate-300 mb-2">
                  <span>{selectedPlanDetails.name} Plan (Monthly)</span>
                  <span>${selectedPlanDetails.price}/mo</span>
                </div>
                {couponStatus === "valid" && couponDetails && (
                  <div className="flex justify-between text-emerald-400 mb-2">
                    <span>Discount ({couponDetails.discount})</span>
                    <span>-{couponDetails.discount}</span>
                  </div>
                )}
                <div className="border-t border-slate-600 pt-2 mt-2 flex justify-between text-white font-bold">
                  <span>Total</span>
                  <span>${selectedPlanDetails.price}/mo</span>
                </div>
              </div>

              {/* Coupon Code */}
              <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
                <Label className="flex items-center gap-2 text-sm font-medium mb-2 text-slate-300">
                  <Tag className="w-4 h-4 text-primary" />
                  Have a promo code?
                </Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      if (couponStatus) {
                        setCouponStatus(null);
                        setCouponDetails(null);
                        setCouponError("");
                      }
                    }}
                    className="uppercase bg-slate-800 border-slate-600 text-white"
                  />
                  <Button
                    variant="outline"
                    onClick={validateCoupon}
                    disabled={!couponCode.trim() || couponStatus === "validating"}
                  >
                    {couponStatus === "validating" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                </div>
                
                {couponStatus === "valid" && couponDetails && (
                  <div className="mt-2 p-2 bg-emerald-900/30 border border-emerald-700 rounded flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-emerald-300">
                      <strong>{couponDetails.discount}</strong> - {couponDetails.name}
                    </span>
                  </div>
                )}
                
                {couponStatus === "invalid" && (
                  <div className="mt-2 p-2 bg-red-900/30 border border-red-700 rounded flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-red-300">{couponError}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <Button 
                  onClick={handleCheckout} 
                  disabled={processing}
                  className="w-full"
                  size="lg"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Start Free Trial — Add Payment Method
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="ghost" 
                  className="w-full text-slate-400"
                  onClick={() => {
                    setSelectedPlan(null);
                    setStep(2);
                  }}
                >
                  ← Choose Different Plan
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                You will be redirected to Stripe for secure payment processing
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
